import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Checkbox,
  TextField,
  FormControlLabel,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { TimeOffRegimeDTO } from 'axiosApi/models/time-off-regime-dto';
import { TimeOffType } from 'axiosApi/models/time-off-type';
import Modal from 'components/common/Modal';
import { handleAPIError } from 'common/errorHandler';
import ErrorSummary from 'components/common/ErrorSummary';

interface AddRegimeModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeId: number;
  onSuccess?: () => void;
}

interface SelectedType {
  id: number;
  name: string;
  availableDays: number;
  validFrom: Date;
  validUntil: Date;
  perks: string[];
  isSelected: boolean;
}

const AddRegimeModal: React.FC<AddRegimeModalProps> = ({
  isOpen,
  onClose,
  employeeId,
  onSuccess
}) => {
  const api = useApi();
  const toast = useToastMessageQueue();
  const [selectedRegimeId, setSelectedRegimeId] = useState<number | null>(null);
  const [availableRegimes, setAvailableRegimes] = useState<TimeOffRegimeDTO[]>([]);
  const [availableTypes, setAvailableTypes] = useState<SelectedType[]>([]);
  const [isLoadingRegimes, setIsLoadingRegimes] = useState(false);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (isOpen) {
      fetchAvailableRegimes();
      resetForm();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedRegimeId) {
      fetchRegimeTypes(selectedRegimeId);
    } else {
      setAvailableTypes([]);
    }
  }, [selectedRegimeId]);

  const resetForm = () => {
    setSelectedRegimeId(null);
    setAvailableTypes([]);
  };

  const fetchAvailableRegimes = async () => {
    if (!api) return;

    setIsLoadingRegimes(true);
    try {
      const response = await api.timeoffApi.apiVversionTimeOffRegimeSearchGet(
        "1",
        undefined,
        0,
        100,
        "name",
        "asc",
        true,
        undefined,
        {}
      );

      if (response.status === 200 && response.data?.data?.queryResult) {
        setAvailableRegimes(response.data.data.queryResult);
      } else {
        setAvailableRegimes([]);
        toast.error({
          header: intl.get('timeOff.toast.error.fetchRegimes'),
        });
      }
    } catch (error) {
      console.error('Error fetching available regimes:', error);
      toast.error({
        header: intl.get('timeOff.toast.error.fetchRegimes'),
        body: error.message,
      });
      setAvailableRegimes([]);
    } finally {
      setIsLoadingRegimes(false);
    }
  };

  const fetchRegimeTypes = async (regimeId: number) => {
    if (!api) return;

    setIsLoadingTypes(true);
    try {
      const response = await api.timeoffApi.apiVversionTimeOffTypeSearchGet("1", regimeId, undefined, 0, 100, "name", "asc");

      if (response.status === 200 && response.data?.data?.queryResult) {
        const types = response.data.data.queryResult.map((type: TimeOffType) => {
          const perks: string[] = [];
          if (type.isBillable) perks.push(intl.get('timeOff.perks.billable'));
          if (type.isCumulative) perks.push(intl.get('timeOff.perks.cumulative'));
          if (type.requiresSpecialApproval) perks.push(intl.get('timeOff.perks.specialApproval'));
          if (type.allowNegativeBalance) perks.push(intl.get('timeOff.perks.negativeBalance'));

          // Default dates: current year from now to end of next year
          const now = new Date();
          const validFrom = type.validFrom ? new Date(type.validFrom) : new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const validUntil = type.validUntil ? new Date(type.validUntil) : new Date(now.getFullYear() + 1, 11, 31); // End of next year

          return {
            id: type.id || 0,
            name: type.name || intl.get('timeOff.common.unknown'),
            availableDays: type.defaultDays || 0,
            validFrom: type.validFrom ? new Date(type.validFrom) : validFrom,
            validUntil: type.validUntil ? new Date(type.validUntil) : validUntil,
            perks,
            isSelected: false
          };
        });

        setAvailableTypes(types);
      } else {
        setAvailableTypes([]);
      }
    } catch (error) {
      console.error('Error fetching regime types:', error);
      toast.error({
        header: intl.get('timeOff.toast.error.fetchTypes'),
        body: error.message,
      });
      setAvailableTypes([]);
    } finally {
      setIsLoadingTypes(false);
    }
  };

  const handleTypeToggle = (typeId: number) => {
    setAvailableTypes(prev =>
      prev.map(type =>
        type.id === typeId
          ? { ...type, isSelected: !type.isSelected }
          : type
      )
    );
  };

  const handleDaysChange = (typeId: number, days: number) => {
    setAvailableTypes(prev =>
      prev.map(type =>
        type.id === typeId
          ? { ...type, availableDays: Math.max(0, days) }
          : type
      )
    );
  };

  const handleValidFromChange = (typeId: number, date: Date | null) => {
    if (!date) return;
    setAvailableTypes(prev =>
      prev.map(type =>
        type.id === typeId
          ? { ...type, validFrom: date }
          : type
      )
    );
  };

  const handleValidUntilChange = (typeId: number, date: Date | null) => {
    if (!date) return;
    setAvailableTypes(prev =>
      prev.map(type =>
        type.id === typeId
          ? { ...type, validUntil: date }
          : type
      )
    );
  };

  const getPerkChipColor = (perk: string) => {
    switch (perk) {
      case intl.get('timeOff.perks.billable'):
        return 'primary';
      case intl.get('timeOff.perks.cumulative'):
        return 'secondary';
      case intl.get('timeOff.perks.specialApproval'):
        return 'warning';
      case intl.get('timeOff.perks.negativeBalance'):
        return 'error';
      default:
        return 'default';
    }
  };

  const handleSubmit = async () => {
    if (!selectedRegimeId) {
      toast.error({
        header: intl.get('validation.required'),
        body: intl.get('timeOff.validation.selectRegime'),
      });
      return;
    }

    const selectedTypes = availableTypes.filter(type => type.isSelected);
    if (selectedTypes.length === 0) {
      toast.error({
        header: intl.get('validation.required'),
        body: intl.get('timeOff.validation.selectAtLeastOneType'),
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const createCommand = {
        idTimeOffRegime: selectedRegimeId,
        idEmployee: employeeId,
        year: new Date().getFullYear() + 1,
        isActive: true,
        employeeTimeOffStocks: selectedTypes.map(type => ({
          name: type.name,
          idTimeOffType: type.id,
          validFrom: type.validFrom,
          validUntil: type.validUntil,
          totalStock: type.availableDays,
          availableStock: type.availableDays
        }))
      };

      const response = await api.timeoffApi.apiVversionEmployeeTimeOffRegimePost(
        "1",
        createCommand
      );

      if (response.status === 200 || response.status === 201) {
        toast.success({
          message: intl.get('timeOff.regime.toast.success')
        });
        onSuccess?.();
        onClose();
      } 
    } catch (error) {
      toast.error({
        header: intl.get(error.response?.data?.errors[0]?.errorLabelKey),
        body: intl.get(error.response?.data?.errors[0]?.errorLabelKey),
      });
      handleAPIError(error.response.data.errors[0], toast, errors);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCount = availableTypes.filter(type => type.isSelected).length;

  return (
    <Modal isOpen={isOpen} closeModal={onClose} size="extraLarge" height="90vh">
      <ErrorSummary errors={errors} showEverything={true} mode="warning" />
      <Box sx={{ position: 'relative', pt: 2 }}>
        <Box sx={{ mb: 4, pr: 5 }}>
          <Typography variant="h5" component="h2" fontWeight="bold">
{intl.get('timeOff.addRegime.title')}
          </Typography>
        </Box>

        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <Box mb={5}>
            <Typography variant="body1" sx={{ mb: 3, fontWeight: 600 }}>
{intl.get('timeOff.addRegime.selectRegime')}
            </Typography>
            <FormControl fullWidth>
              <Select
                value={selectedRegimeId || ''}
                onChange={(e) => setSelectedRegimeId(Number(e.target.value) || null)}
                disabled={isSubmitting || isLoadingRegimes}
                displayEmpty
                sx={{ 
                  '& .MuiSelect-select': { 
                    py: 1.5 
                  }
                }}
              >
                <MenuItem value="">
                  <em>{intl.get('timeOff.addRegime.selectRegimePlaceholder')}</em>
                </MenuItem>
                {availableRegimes.map((regime) => (
                  <MenuItem key={regime.id} value={regime.id}>
                    {regime.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {selectedRegimeId && (
            <Box>
              {isLoadingTypes ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                  <CircularProgress />
                </Box>
              ) : availableTypes.length > 0 ? (
                <Box>
                  <Box sx={{
                    mb: 2,
                    p: 2,
                    backgroundColor: 'grey.100',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.300'
                  }}>
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 2.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold">{intl.get('timeOff.table.name')}</Typography>
                      </Grid>
                      <Grid size={{ xs: 1.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold" textAlign="center">{intl.get('timeOff.table.days')}</Typography>
                      </Grid>
                      <Grid size={{ xs: 2.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold" textAlign="center">{intl.get('timeOff.table.validFrom')}</Typography>
                      </Grid>
                      <Grid size={{ xs: 2.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold" textAlign="center">{intl.get('timeOff.table.validUntil')}</Typography>
                      </Grid>
                      <Grid size={{ xs: 3 }}>
                        <Typography variant="subtitle2" fontWeight="bold" textAlign="center">{intl.get('timeOff.table.perks')}</Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                    {availableTypes.map((type, index) => (
                      <Box
                        key={type.id}
                        sx={{
                          borderBottom: index === availableTypes.length - 1 ? 'none' : '1px solid',
                          borderColor: 'grey.200',
                          '&:hover': { backgroundColor: 'grey.50' }
                        }}
                      >
                        <Grid container spacing={3} sx={{ p: 3, alignItems: 'center' }}>
                          <Grid size={{ xs: 2.5 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={type.isSelected}
                                  onChange={() => handleTypeToggle(type.id)}
                                  disabled={isSubmitting}
                                  color="primary"
                                />
                              }
                              label={
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                  {type.name}
                                </Typography>
                              }
                            />
                          </Grid>
                          <Grid size={{ xs: 1.5 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={type.availableDays}
                              onChange={(e) => handleDaysChange(type.id, parseInt(e.target.value, 10) || 0)}
                              disabled={isSubmitting || !type.isSelected}
                              inputProps={{ min: 0, style: { textAlign: 'center' } }}
                              sx={{
                                '& .MuiInputBase-root': {
                                  backgroundColor: type.isSelected ? 'background.paper' : 'grey.100'
                                }
                              }}
                            />
                          </Grid>
                          <Grid size={{ xs: 2.5 }}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DatePicker
                                value={type.validFrom}
                                onChange={(date) => handleValidFromChange(type.id, date)}
                                disabled={isSubmitting || !type.isSelected}
                                slotProps={{
                                  textField: {
                                    size: 'small',
                                    sx: {
                                      '& .MuiInputBase-root': {
                                        backgroundColor: type.isSelected ? 'background.paper' : 'grey.100'
                                      }
                                    }
                                  }
                                }}
                              />
                            </LocalizationProvider>
                          </Grid>
                          <Grid size={{ xs: 2.5 }}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DatePicker
                                value={type.validUntil}
                                onChange={(date) => handleValidUntilChange(type.id, date)}
                                disabled={isSubmitting || !type.isSelected}
                                slotProps={{
                                  textField: {
                                    size: 'small',
                                    sx: {
                                      '& .MuiInputBase-root': {
                                        backgroundColor: type.isSelected ? 'background.paper' : 'grey.100'
                                      }
                                    }
                                  }
                                }}
                              />
                            </LocalizationProvider>
                          </Grid>
                          <Grid size={{ xs: 3 }}>
                            <Box display="flex" gap={0.5} flexWrap="wrap" justifyContent="center">
                              {type.perks.map((perk, perkIndex) => (
                                <Chip
                                key={perkIndex}
                                label={perk}
                                size="small"
                                color={getPerkChipColor(perk) as any}
                                variant="outlined"
                            />
                              ))}
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center">
{intl.get('timeOff.addRegime.noTypesFound')}
                </Typography>
              )}
            </Box>
          )}
          <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 4,
            pt: 3,
            borderTop: '1px solid',
            borderColor: 'grey.200'
          }}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isSubmitting}
              size="small"
              sx={{ minWidth: 120 }}
            >
{intl.get('common.cancel')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={isSubmitting || !selectedRegimeId || selectedCount === 0}
              startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
              size="small"
              sx={{ minWidth: 120 }}
            >
{intl.get('common.confirm')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddRegimeModal;
